import React, { useContext, useEffect, useRef, useState } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import Spinner from "get-life-storybook-ts/lib/components/Icons/Spinner";
import DashboardTopBar from "../../dashboard/components/DashboardTopBar";
import NativeShellFooter from "../../../../../components/NativeShell/NativeShellFooter";
import { I18nContext } from "../../../../../context/i18n.context";
import { DOMAIN } from "../../../../../constants/Global";
import { PHONE_POST } from "../../../../../constants/Globals";
import { GlobalPositionEmails } from "../../../../../utils/InternationlChanges";
import {
  confirmRenewalWithToken,
  NativeRenewalConfirmationOutcome,
} from "../../../../../api/request/Lead/RenewalConfirmation.service";
import "./NativeRenewalConfirmation.scss";

// Las renovaciones con este enlace son de AGL (Italia): soporte de Italia.
const SUPPORT_EMAIL = GlobalPositionEmails.it;
const SUPPORT_PHONE = "02 8294 21 28";

type State = { kind: "PENDING" } | NativeRenewalConfirmationOutcome;

/**
 * Confirmación de renovación con el enlace native del aviso (F3.4 P4, D20). Una
 * sola llamada al gateway con el token del enlace; solo se felicita al cliente
 * si la confirmación ha entrado de verdad. Marco del Portal Cliente native.
 */
const NativeRenewalConfirmation = ({ token }: { token: string }) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  // translate() devuelve la clave si falta la traducción: fallback IT.
  const t = (key: string, fallback: string): string => {
    const value = translate(key);
    return value === key ? fallback : value;
  };
  const [state, setState] = useState<State>({ kind: "PENDING" });
  const sent = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    // Una sola confirmación por visita (StrictMode monta dos veces en desarrollo).
    if (sent.current) {
      return;
    }
    sent.current = true;
    confirmRenewalWithToken(token).then(setState);
  }, [token]);

  const contact = (
    <p className="nativeRenewal__contact">
      {t("renewalConfirmation.native.contact", "Scrivici a")}{" "}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{" "}
      {t("renewalConfirmation.native.orCall", "o chiamaci allo")}{" "}
      <a href={`tel:${SUPPORT_PHONE.replace(/\s/g, "")}`}>{SUPPORT_PHONE}</a>.
    </p>
  );

  const body = (): React.ReactNode => {
    switch (state.kind) {
      case "PENDING":
        return (
          <div className="nativeRenewal__pending" aria-busy="true">
            <span className="nativeRenewal__spinner">
              <Spinner />
            </span>
            <p>{t("renewalConfirmation.native.pending", "Stiamo confermando il rinnovo…")}</p>
          </div>
        );
      case "CONFIRMED":
        return (
          <>
            <span className="nativeRenewal__icon nativeRenewal__icon--ok">
              <GlobalIcon iconName="CheckedIcon" size="M" color="currentColor" />
            </span>
            <h1>{t("renewalConfirmation.native.confirmedTitle", "Rinnovo confermato")}</h1>
            <p>
              {t(
                "renewalConfirmation.native.confirmedText",
                "Grazie! Il rinnovo della tua polizza è confermato. Ti abbiamo inviato un'email con il riepilogo."
              )}
            </p>
          </>
        );
      case "ALREADY_CONFIRMED":
        return (
          <>
            <span className="nativeRenewal__icon nativeRenewal__icon--ok">
              <GlobalIcon iconName="CheckedIcon" size="M" color="currentColor" />
            </span>
            <h1>{t("renewalConfirmation.native.alreadyTitle", "Rinnovo già confermato")}</h1>
            <p>
              {t(
                "renewalConfirmation.native.alreadyText",
                "Avevi già confermato il rinnovo della tua polizza: non devi fare nient'altro."
              )}
            </p>
          </>
        );
      case "EXPIRED":
        return (
          <>
            <span className="nativeRenewal__icon nativeRenewal__icon--warn">
              <GlobalIcon iconName="InfoIcon" size="M" color="currentColor" />
            </span>
            <h1>{t("renewalConfirmation.native.expiredTitle", "Il link è scaduto")}</h1>
            <p>
              {t(
                "renewalConfirmation.native.expiredText",
                "Il periodo per confermare il rinnovo online è terminato. Contattaci e ti aiutiamo noi."
              )}
            </p>
            {contact}
          </>
        );
      default:
        return (
          <>
            <span className="nativeRenewal__icon nativeRenewal__icon--warn">
              <GlobalIcon iconName="InfoIcon" size="M" color="currentColor" />
            </span>
            <h1>
              {t("renewalConfirmation.native.errorTitle", "Non siamo riusciti a confermare il rinnovo")}
            </h1>
            <p>
              {t(
                "renewalConfirmation.native.errorText",
                "Il rinnovo non è stato confermato. Riprova più tardi o contattaci."
              )}
            </p>
            {contact}
          </>
        );
    }
  };

  return (
    <div className="nativeRenewal" style={{ "--shell-width": "640px" } as React.CSSProperties}>
      <DashboardTopBar showAllianz={"it" === DOMAIN} phone={PHONE_POST} brand="always" />
      <main className="nativeRenewal__page">
        <p className="nativeRenewal__label">{t("renewalConfirmation.native.label", "Rinnovo")}</p>
        <section className="nativeRenewal__card" data-testid="native-renewal" data-state={state.kind}>
          {body()}
        </section>
      </main>
      <NativeShellFooter />
    </div>
  );
};

export default NativeRenewalConfirmation;
